import shaderHelper from "../helpers/shaderHelper";
import fboHelper from "../helpers/fboHelper";
import mathUtils from "../utils/mathUtils";
import * as glslify from 'glslify';
import * as THREE from 'three';

export default class Mipmapper {


    constructor() {
        this.root = 14;
        this.fpoHelper = fboHelper;
        this.tmpRenderTarget = this.fpoHelper.createRenderTarget(1, 1, true);
        /** @type {number} */
        this.maxMipmapLevel = 0;
        this.textureSize = new THREE.Vector2;
        /** @type {number} */
        var config = this.root + 1;
        this.geometry = this.init(config);
        this.oddGeometry = this.init(config);

        shaderHelper.addChunk("lodMipmapSample", glslify(["#define GLSLIFY 1\nvec2 getMipUv (vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\tfloat isNonZeroLod = step(0.5, lod);\n\tvec2 xy = vec2(\n\t\tisNonZeroLod * textureSize.x / screenTextureSize.x,\n\t\t1.0 - 1.0 / pow(2.0, max(0.0, lod - 1.0))\n\t);\n\tvec2 wh = (max(vec2(1.0), floor(textureSize / pow(2.0, lod))));\n\treturn xy + (0.5 + uv * (wh - 1.0)) / screenTextureSize;\n}\n\nvec2 getMipUv (vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize, out vec4 clampRect) {\n\tfloat isNonZeroLod = step(0.5, lod);\n\tvec2 xy = vec2(\n\t\tisNonZeroLod * textureSize.x / screenTextureSize.x,\n\t\t1.0 - 1.0 / pow(2.0, max(0.0, lod - 1.0))\n\t);\n\tvec2 wh = (max(vec2(1.0), floor(textureSize / pow(2.0, lod))));\n\tvec2 halfTexelSize = 0.5 / screenTextureSize;\n\n\tclampRect = vec4(\n\t\txy + halfTexelSize,\n\t\txy + wh / screenTextureSize - halfTexelSize\n\t);\n\t\n\treturn xy + (0.5 + uv * (wh - 1.0)) / screenTextureSize;\n}\n\nvec4 lodMipmapSampleNearestCubic (sampler2D tex, vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\tvec4 clampRect;\n\tuv = getMipUv(uv, lod, textureSize, screenTextureSize, clampRect);\n\treturn textureBicubic(tex, uv, screenTextureSize, clampRect);\n}\n\nvec4 lodMipmapSampleNearestSmoothstep (sampler2D tex, vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\treturn textureSmoothstep(tex, getMipUv(uv, lod, textureSize, screenTextureSize), screenTextureSize);\n}\n\nvec4 lodMipmapSampleNearest (sampler2D tex, vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\treturn texture2D(tex, getMipUv(uv, lod, textureSize, screenTextureSize));\n}\n\nvec4 lodMipmapSampleCubic (sampler2D tex, vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\tfloat lodFloor = floor(lod);\n\tfloat lodCeil = lodFloor + 1.0;\n\tfloat lodFract = lod - lodFloor;\n\n\tvec4 loClamp, hiClamp;\n\tvec2 loUv = getMipUv(uv, lodFloor, textureSize, screenTextureSize, loClamp);\n\tvec2 hiUv = getMipUv(uv, lodCeil, textureSize, screenTextureSize, hiClamp);\n\treturn mix(\n\t\ttextureBicubic(tex, loUv, screenTextureSize, loClamp),\n\t\ttextureBicubic(tex, hiUv, screenTextureSize, hiClamp),\n\t\tlodFract\n\t);\n}\n\nvec4 lodMipmapSampleSmoothstep (sampler2D tex, vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\tfloat lodFloor = floor(lod);\n\tfloat lodCeil = lodFloor + 1.0;\n\tfloat lodFract = lod - lodFloor;\n\treturn mix(\n\t\ttextureSmoothstep(tex, getMipUv(uv, lodFloor, textureSize, screenTextureSize), screenTextureSize),\n\t\ttextureSmoothstep(tex, getMipUv(uv, lodCeil, textureSize, screenTextureSize), screenTextureSize),\n\t\tlodFract\n\t);\n}\n\nvec4 lodMipmapSample (sampler2D tex, vec2 uv, float lod, vec2 textureSize, vec2 screenTextureSize) {\n\tfloat lodFloor = floor(lod);\n\tfloat lodCeil = lodFloor + 1.0;\n\tfloat lodFract = lod - lodFloor;\n\treturn mix(\n\t\ttexture2D(tex, getMipUv(uv, lodFloor, textureSize, screenTextureSize)),\n\t\ttexture2D(tex, getMipUv(uv, lodCeil, textureSize, screenTextureSize)),\n\t\tlodFract\n\t);\n}\n"]));

    }

    /**
     * @param {number} testSuite
     * @return {?}
     */
    init(testSuite) {
        /** @type {!Float32Array} */
        var line_positions = new Float32Array(6 * testSuite * 2);
        /** @type {!Float32Array} */
        var classifications = new Float32Array(6 * testSuite * 3);
        var geometry = new THREE.BufferGeometry;
        return geometry.setAttribute("position", new THREE.BufferAttribute(classifications, 3)), geometry.setAttribute("uv", new THREE.BufferAttribute(line_positions, 2)), geometry;
    }

    /**
     * @param {number} height
     * @param {number} width
     * @param {undefined} t
     * @param {number} h
     * @return {undefined}
     */
    resize(height, width, t, h) {
        this.maxMipmapLevel = mathUtils.powerTwoFloorBase(width);
        this.tmpRenderTarget.setSize(t, h);
        this.textureSize.set(t, h);
        var normals = this.geometry.attributes.position.array;
        var d = this.geometry.attributes.uv.array;
        /** @type {number} */
        var i = 0;
        /** @type {number} */
        var downScalePower = 0;
        var l$jscomp$341 = this.maxMipmapLevel;
        for (; downScalePower < l$jscomp$341; downScalePower++) {
            /** @type {number} */
            var r = (0 < downScalePower ? height : 0) / t;
            /** @type {number} */
            var y = (h - (h >> Math.max(0, downScalePower - 1))) / h;
            /** @type {number} */
            var z = Math.max(1, height >> downScalePower) / t;
            /** @type {number} */
            var o = Math.max(1, width >> downScalePower) / h;
            /** @type {number} */
            d[i + 0] = r;
            /** @type {number} */
            d[i + 1] = y + o;
            /** @type {number} */
            d[i + 2] = r;
            /** @type {number} */
            d[i + 3] = y;
            /** @type {number} */
            d[i + 4] = r + z;
            /** @type {number} */
            d[i + 5] = y + o;
            /** @type {number} */
            d[i + 6] = r + z;
            /** @type {number} */
            d[i + 7] = y + o;
            /** @type {number} */
            d[i + 8] = r;
            /** @type {number} */
            d[i + 9] = y;
            /** @type {number} */
            d[i + 10] = r + z;
            /** @type {number} */
            d[i + 11] = y;
            /** @type {number} */
            i = i + 12;
        }
        var p = this.oddGeometry.attributes.uv.array;
        var ceil_pts = this.oddGeometry.attributes.position.array;
        /** @type {number} */
        var index = i = 0;
        /** @type {number} */
        var root = 0;
        /** @type {number} */
        var ceil_idx = 0;
        /** @type {number} */
        var targetOffsetHeight = 0;
        /** @type {number} */
        var clientHeight = this.maxMipmapLevel - 1;
        for (; targetOffsetHeight < clientHeight; targetOffsetHeight++) {
            /** @type {number} */
            normals[index + 0] = 2 * d[i + 12] - 1;
            /** @type {number} */
            normals[index + 1] = 2 * d[i + 13] - 1;
            /** @type {number} */
            normals[index + 3] = 2 * d[i + 14] - 1;
            /** @type {number} */
            normals[index + 4] = 2 * d[i + 15] - 1;
            /** @type {number} */
            normals[index + 6] = 2 * d[i + 16] - 1;
            /** @type {number} */
            normals[index + 7] = 2 * d[i + 17] - 1;
            /** @type {number} */
            normals[index + 9] = 2 * d[i + 18] - 1;
            /** @type {number} */
            normals[index + 10] = 2 * d[i + 19] - 1;
            /** @type {number} */
            normals[index + 12] = 2 * d[i + 20] - 1;
            /** @type {number} */
            normals[index + 13] = 2 * d[i + 21] - 1;
            /** @type {number} */
            normals[index + 15] = 2 * d[i + 22] - 1;
            /** @type {number} */
            normals[index + 16] = 2 * d[i + 23] - 1;
            if ((targetOffsetHeight + 1) % 2) {
                /** @type {number} */
                ceil_pts[ceil_idx + 0] = 2 * (p[root + 0] = d[i + 12]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 1] = 2 * (p[root + 1] = d[i + 13]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 3] = 2 * (p[root + 2] = d[i + 14]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 4] = 2 * (p[root + 3] = d[i + 15]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 6] = 2 * (p[root + 4] = d[i + 16]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 7] = 2 * (p[root + 5] = d[i + 17]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 9] = 2 * (p[root + 6] = d[i + 18]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 10] = 2 * (p[root + 7] = d[i + 19]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 12] = 2 * (p[root + 8] = d[i + 20]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 13] = 2 * (p[root + 9] = d[i + 21]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 15] = 2 * (p[root + 10] = d[i + 22]) - 1;
                /** @type {number} */
                ceil_pts[ceil_idx + 16] = 2 * (p[root + 11] = d[i + 23]) - 1;
                /** @type {number} */
                root = root + 12;
                /** @type {number} */
                ceil_idx = ceil_idx + 18;
            }
            /** @type {number} */
            i = i + 12;
            /** @type {number} */
            index = index + 18;
        }
        /** @type {boolean} */
        this.geometry.attributes.uv.needsUpdate = true;
        /** @type {boolean} */
        this.geometry.attributes.position.needsUpdate = true;
        /** @type {boolean} */
        this.oddGeometry.attributes.uv.needsUpdate = true;
        /** @type {boolean} */
        this.oddGeometry.attributes.position.needsUpdate = true;
    };

    /**
     * @param {!Object} obj
     * @return {undefined}
     */
    render(obj) {
        var canvas = this.fpoHelper.getColorState();
        var sprite = this.fpoHelper.uvCopyMaterial;
        if (!(this.textureSize.x == obj.width && this.textureSize.y == obj.height)) {
            obj.setSize(obj.width, obj.height);
            this.resize(Math.round(obj.width / 3 * 2), obj.height, obj.width, obj.height);
        }
        /** @type {boolean} */
        this.fpoHelper.renderer.autoClearColor = false;
        /** @type {!Object} */
        var p = obj;
        var key = this.tmpRenderTarget;
        /** @type {number} */
        var count = 0;
        /** @type {number} */
        var targetOffsetHeight = 0;
        /** @type {number} */
        var clientHeight = this.maxMipmapLevel - 1;
        for (; targetOffsetHeight < clientHeight; targetOffsetHeight++) {
            sprite.uniforms.u_texture.value = p.texture;
            /** @type {number} */
            this.geometry.drawRange.start = 6 * targetOffsetHeight;
            /** @type {number} */
            this.geometry.drawRange.count = 6;
            this.fpoHelper.renderGeometry(this.geometry, sprite, key);
            if (key === this.tmpRenderTarget) {
                count++;
            }
            var tmp = key;
            key = p;
            p = tmp;
        }
        /** @type {number} */
        this.oddGeometry.drawRange.start = 0;
        /** @type {number} */
        this.oddGeometry.drawRange.count = 6 * count;
        sprite.uniforms.u_texture.value = this.tmpRenderTarget.texture;
        this.fpoHelper.renderGeometry(this.oddGeometry, sprite, obj);
        this.fpoHelper.setColorState(canvas);
    };

}
